import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { StatePair } from '../UnicronApp/preloader/StatePair';
import { ChildrenOnly } from './types/ChildrenOnly';
import { TagLike } from './util/TagLike';
import { returnVoid } from './util/returnVoid';

/** Combines context and portals to allow components to render into other
 * components elsewhere in the tree.
 *
 * Both components must have a `<Coordinator />` as their ancestor.
 * Then one component can put children inside an `<Input />` and they will
 * appear inside the corresponding `<Output />`.
 *  */
export interface Teleport {
  Input: (props: ChildrenOnly) => JSX.Element | null;
  Output: TagLike<'div'>;
  Coordinator: (props: ChildrenOnly) => JSX.Element;
}

export const createTeleport = (): Teleport => {
  const context = createContext<StatePair<HTMLDivElement | null>>([null, returnVoid]);

  const Input: Teleport['Input'] = ({ children }) => {
    const [output] = useContext(context);
    return output ? createPortal(children, output) : null;
  };

  const Output: Teleport['Output'] = props => {
    const [, setOutput] = useContext(context);
    return <div ref={setOutput} {...props} />;
  };

  const Coordinator: Teleport['Coordinator'] = ({ children }) => {
    const state = useState<HTMLDivElement | null>(null);

    return <context.Provider value={state}>{children}</context.Provider>;
  };

  return { Input, Output, Coordinator };
};
